import { useState } from 'react';
import { api } from '../../lib/api';
import { extractApiErrorMessage } from '../../lib/api-error';

type Mode = 'analyze_only' | 'analyze_with_re_stt';

interface DryRunResult {
  count: number;
  estimatedCostMicros: number;
  estimatedDurationMinutes: number;
}

interface StartResult {
  jobId: string;
  total: number;
  processSessionId: string;
}

interface CeilingError {
  error: 'EXCEEDS_CEILING';
  count: number;
  ceiling: number;
  detail: string;
}

function formatCost(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

export function AiBulkAnalyzeSection(): JSX.Element {
  const [mode, setMode] = useState<Mode>('analyze_only');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [phoneNumbersRaw, setPhoneNumbersRaw] = useState('');
  const [callIdsRaw, setCallIdsRaw] = useState('');
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [runBusy, setRunBusy] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [ceilingWarning, setCeilingWarning] = useState<CeilingError | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  function buildFilter(): Record<string, unknown> {
    const filter: Record<string, unknown> = {};
    if (dateFrom) filter.dateFrom = new Date(dateFrom).toISOString();
    if (dateTo) filter.dateTo = new Date(dateTo).toISOString();
    if (campaignId.trim()) filter.campaignId = campaignId.trim();
    const phoneNumbers = phoneNumbersRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (phoneNumbers.length > 0) filter.phoneNumbers = phoneNumbers;
    const ids = callIdsRaw
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length > 0) filter.callIds = ids;
    return filter;
  }

  async function handleDryRun(): Promise<void> {
    setDryRunBusy(true);
    setDryRunResult(null);
    setCeilingWarning(null);
    setMsg(null);
    try {
      const res = await api.post<DryRunResult>('/ai/bulk-analyze', {
        mode,
        filter: buildFilter(),
        dryRun: true,
      });
      setDryRunResult(res);
    } catch (err) {
      setMsg({ ok: false, text: extractApiErrorMessage(err, 'Dry-run failed.') });
    } finally {
      setDryRunBusy(false);
    }
  }

  async function handleRun(acknowledgeOverride = false): Promise<void> {
    if (!dryRunResult) {
      setMsg({ ok: false, text: 'Run a dry-run first to confirm the row count and cost estimate.' });
      return;
    }
    setRunBusy(true);
    setMsg(null);
    setCeilingWarning(null);
    try {
      const res = await api.post<StartResult>('/ai/bulk-analyze', {
        mode,
        filter: buildFilter(),
        ...(acknowledgeOverride ? { acknowledgeOverride: true } : {}),
      });
      setMsg({ ok: true, text: `Job started: ${res.jobId} (${res.total} rows). Watch progress in the AI jobs panel.` });
    } catch (err) {
      // The server may respond with EXCEEDS_CEILING — surface it so the
      // admin can confirm the override.
      const detail = (err as { detail?: CeilingError }).detail;
      if (detail && detail.error === 'EXCEEDS_CEILING') {
        setCeilingWarning(detail);
      } else {
        setMsg({ ok: false, text: extractApiErrorMessage(err, 'Run failed.') });
      }
    } finally {
      setRunBusy(false);
    }
  }

  return (
    <section className="card p-4 space-y-4">
      <header className="border-b border-ink-200 pb-3">
        <h2 className="text-sm font-semibold text-ink-900">Bulk analyze</h2>
        <p className="text-xs text-ink-500 mt-0.5">
          Re-run analysis across a date range, campaign, phone number, or list of call IDs. Always dry-run first to confirm
          the row count and cost. Re-STT mode forces a fresh transcript (skips the cache) — slower and more
          expensive.
        </p>
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Mode</label>
          <select className="input" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
            <option value="analyze_only">Analyze only (reuse existing transcript)</option>
            <option value="analyze_with_re_stt">Analyze + re-STT (fresh transcript)</option>
          </select>
        </div>
        <div className="md:col-span-2 grid grid-cols-2 gap-3">
          <div>
            <label className="label">Date from</label>
            <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="label">Date to</label>
            <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Campaign ID (optional)</label>
          <input className="input" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} />
        </div>
        <div>
          <label className="label">Phone numbers (optional)</label>
          <textarea
            className="input font-mono text-xs"
            rows={3}
            value={phoneNumbersRaw}
            onChange={(e) => setPhoneNumbersRaw(e.target.value)}
            placeholder="+447911123456, 07911 123456"
          />
          <p className="text-[11px] text-ink-500 mt-1">
            Matches recordings where the number appears as caller or destination.
          </p>
        </div>
        <div className="md:col-span-2">
          <label className="label">Call IDs (one per line or comma-separated, optional)</label>
          <textarea
            className="input font-mono text-xs"
            rows={3}
            value={callIdsRaw}
            onChange={(e) => setCallIdsRaw(e.target.value)}
            placeholder="cmxxxx, cmyyyy, cmzzzz"
          />
          <p className="text-[11px] text-ink-500 mt-1">
            When provided, overrides date / campaign / phone filters.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-secondary" onClick={() => void handleDryRun()} disabled={dryRunBusy}>
          {dryRunBusy ? 'Estimating…' : 'Dry-run'}
        </button>
        <button
          className="btn-primary"
          onClick={() => void handleRun(false)}
          disabled={runBusy || !dryRunResult || dryRunResult.count === 0}
        >
          {runBusy ? 'Starting…' : `Run live${dryRunResult ? ` (${dryRunResult.count} rows)` : ''}`}
        </button>
        {dryRunResult && (
          <span className="text-xs text-ink-700">
            ~{formatCost(dryRunResult.estimatedCostMicros)} · ~{dryRunResult.estimatedDurationMinutes} min
          </span>
        )}
      </div>

      {ceilingWarning && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1.5">
          <div>
            <span className="font-semibold">Row ceiling exceeded.</span> {ceilingWarning.detail}
          </div>
          <button
            type="button"
            className="btn-secondary !text-xs"
            onClick={() => void handleRun(true)}
            disabled={runBusy}
          >
            Override and run anyway ({ceilingWarning.count} rows)
          </button>
        </div>
      )}

      {msg && (
        <p className={`text-sm ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>
      )}
    </section>
  );
}
